import {useState, useMemo, type ReactNode, type SyntheticEvent} from 'react'
import {Accordion, AccordionSummary, AccordionDetails, Typography, Box} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LandscapeIcon from '@mui/icons-material/Landscape'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import {useTranslation} from 'react-i18next'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import type {SvgIconComponent} from '@mui/icons-material'
import SceneSettingsPanel from './SceneSettingsPanel'
import LightingShadowPanel from './LightingShadowPanel'
import DeviceMaterialsPanel from './DeviceMaterialsPanel'
import DeviceShapesPanel from './DeviceShapesPanel'
import DeviceFrontFacePanel from './DeviceFrontFacePanel'
import DeviceSideEdgesPanel from './DeviceSideEdgesPanel'
import DeviceSelectPanel from './DeviceSelectPanel'
import DeviceRotationPanel from './DeviceRotationPanel'
import DeviceDepthPanel from './DeviceDepthPanel'

interface SidebarSection {
  id: string
  titleKey: string
  icon: SvgIconComponent
  content: ReactNode
}

const SECTIONS: SidebarSection[] = [
  {
    id: 'scene',
    titleKey: 'sidebar.scene',
    icon: LandscapeIcon,
    content: (
      <>
        <SceneSettingsPanel />
        <LightingShadowPanel />
      </>
    ),
  },
  {
    id: 'device',
    titleKey: 'sidebar.device',
    icon: PhoneIphoneIcon,
    content: (
      <>
        <DeviceSelectPanel />
        <DeviceRotationPanel />
        <DeviceDepthPanel />
        <DeviceShapesPanel />
        <DeviceFrontFacePanel />
        <DeviceSideEdgesPanel />
        <DeviceMaterialsPanel />
      </>
    ),
  },
]

// ── LeftSidebarMenu — left panel with scene + device settings ─────

const LeftSidebarMenu = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const [expanded, setExpanded] = useState<string | false>('scene')

  const handleChange = (panel: string) => (_: SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  const sx = useMemo(() => buildStyles(tokens), [tokens])

  return (
    <Box sx={sx.root}>
      <Box sx={sx.accordionArea}>
        {SECTIONS.map((section) => {
          const Icon = section.icon
          return (
            <Accordion
              key={section.id}
              expanded={expanded === section.id}
              onChange={handleChange(section.id)}
              disableGutters
              square
              sx={sx.accordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.summary}>
                <Icon sx={sx.sectionIcon} />
                <Typography sx={sx.sectionTitle}>{t(section.titleKey)}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={sx.details}>{section.content}</AccordionDetails>
            </Accordion>
          )
        })}
      </Box>
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    width: tokens.sidebar.width,
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: tokens.sidebar.background,
    borderRight: `1px solid ${tokens.sidebar.border}`,
    flexShrink: 0,
  },
  accordionArea: {
    flex: 1,
    overflowY: 'auto' as const,
  },
  accordion: {
    backgroundColor: 'transparent',
    color: tokens.text.primary,
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  summary: {
    borderBottom: `1px solid ${tokens.accordion.border}`,
    minHeight: 48,
    '&.Mui-expanded': {minHeight: 48},
    '& .MuiAccordionSummary-content': {
      margin: '12px 0',
      alignItems: 'center',
      gap: '8px',
      '&.Mui-expanded': {margin: '12px 0'},
    },
  },
  expandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 20,
  },
  sectionIcon: {
    color: tokens.accordion.sectionIcon,
    fontSize: 18,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: tokens.text.primary,
  },
  details: {
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.accordion.border}`,
  },
})

export default LeftSidebarMenu
